//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  ClaimsProcessingGlobalStyle,
} from './ClaimsProcessing.styles';

const hero = {
  slug: 'claims-processing',
  title: 'Claims Processing',
  description:
    'Gathers the documentation, validates the claim against the shipment record, and drafts the filing for your approval — so claims get filed and recovered instead of written off.',
  metric: '2x',
  metricLabel: 'faster claims recovery, with higher filing rates',
};

const watchItRun = {
  title: 'Concealed damage on delivery',
  stageHeadTitle:
    'Receiver just reported concealed damage on delivered load #LS-47788.',
  stageHeadMeta: 'Claims · #LS-47788',
  rows: [
    {
      time: '10:20:00',
      text: 'Concealed-damage claim opened on #LS-47788.',
    },
    {
      time: '10:20:15',
      text: 'Pulled the BOL, POD, photos and delivery notes automatically.',
    },
    {
      time: '10:20:40',
      text: 'Validated against carrier liability and the commodity value.',
    },
    {
      time: '10:21:10',
      text: 'Drafted the claim packet — ready for your one-click approval.',
    },
    {
      time: '10:21:12',
      text: 'Filed the moment you approve. Nothing left on the table.',
    },
  ],
  summaryText:
    'Concealed damage usually goes unclaimed because filing takes too long. Loadsmart AI gathered every document, validated the claim against carrier liability, and drafted the packet for your approval — turning a write-off into recovered dollars.',
  agentBadge: 'Claims Agent',
  escalationTitle: 'The approval stays yours.',
  escalationText:
    'This agent stops short of filing on its own. It gathers the documents, validates the claim and drafts the packet — the work that used to sit in a queue — then waits for you. Your team decides what gets filed; the paperwork stops being the bottleneck.',
};

const howItWorks = {
  stepsTitleWhite: 'Builds the claim.',
  stepsTitleGradient: 'You approve it.',
  steps: [
    {
      title: 'Gathers the documentation',
      description:
        'Pulls the bill of lading, proof of delivery, photos and original quote automatically, from the records already captured on every shipment.',
    },
    {
      title: 'Validates against the record',
      description:
        'Checks the reported shortage or damage against what was ordered, quoted and delivered — catching mismatches before they become a rejected claim.',
    },
    {
      title: 'Drafts the response',
      description:
        'Your team reviews a finished, documented claim instead of building one from scratch under deadline.',
    },
    {
      title: 'Files inside the window',
      description:
        'Once you approve, the claim goes to the carrier while the details are still fresh.',
    },
  ],
  outcomes: [
    {
      title: 'Small claims stop getting written off',
      description:
        'Small claims used to get skipped because they weren’t worth a half day’s work. Now they take minutes.',
    },
    {
      title: 'Claims move while details are fresh',
      description:
        'Less back and forth over documentation, and more claims approved on the first pass.',
    },
    {
      title: 'The audit trail holds up',
      description:
        'Every claim is backed by records validated when the shipment happened, so there’s nothing to reconstruct later.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description: 'Captures the BOLs and PODs every claim is built from.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description: 'Prevents the charges this one has to recover.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description: 'One of the six agents that make up Loadsmart AI.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description: 'One of the six agents that make up Loadsmart AI.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'One of the six agents that make up Loadsmart AI.',
  },
];

function ClaimsProcessing() {
  return (
    <PageWrapper>
      <ClaimsProcessingGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Claims Processing" />
      <Footer />
    </PageWrapper>
  );
}

export default ClaimsProcessing;
export { ClaimsProcessing };
